import {
	TemplateVariable,
	TemplateVariableValues,
	TemplateVariableNumber,
	TemplateVariableString,
	TemplateVariableArray
} from '../../../../models/product';
import { ProductTranslationService, removeTranslationCode } from '../../../../services/product-translation.service';
import { CharacteristicSet, CharacteristicValueSimpleObject } from '../../../../models/product-configuration';

import { FieldObjectCst } from './field/field-object';
import { Field, FieldValue, FieldValueObject } from './field/field';
import { FieldNumberCst, FieldNumberVar } from './field/field-number';
import { FieldTextCst, FieldTextVar, FieldTextSelection } from './field/field-text';
import { FieldListCst, FieldListVar } from './field/field-list';

const FN_SOLD_TO_PARTY = 'soldToParty';
const FN_SHIP_TO_PARTY = 'shipToParty';
const FN_END_USER = 'endUser';

export function dynObjectToMap<T>(dynObject: Record<string, T>) {
	const map = new Map<string, T>();
	for (const propertyName of Object.getOwnPropertyNames(dynObject)) {
		map.set(propertyName, dynObject[propertyName]);
	}
	return map;
}

function buildFieldNumber(
	name: string,
	orignalValue: number | undefined,
	value: number | undefined,
	variable: TemplateVariableNumber | undefined,
	allowUndefinedCstValue: boolean
) {
	if (variable === undefined) {
		return allowUndefinedCstValue || value !== undefined
			? new FieldNumberCst(name, orignalValue, value)
			: undefined;
	}
	/* e.g., templateVariable.maximum = '999.99'
		* - index of '.': 3
		* - integer count: 3	= index of '.'
		* - decimal count: 2	= length - index of '.' - 1
		*
		* e.g., templateVariable.maximum = '99'
		* - index of '.': -1
		* - integer count: 2	= length
		* - decimal count: 0
		*/
	const i = variable.maximum.indexOf('.');
	const integerCount = i === -1 ? variable.maximum.length : i;
	const decimalCount = i === -1 ? 0 : variable.maximum.length - i - 1;
	return new FieldNumberVar(name, variable.path, orignalValue, value, integerCount, decimalCount);
}

function buildFieldText(
	name: string,
	originalValue: string | undefined,
	value: string | undefined,
	variable: TemplateVariableString | undefined,
	variableValues: TemplateVariableValues | undefined,
	allowUndefinedCstValue: boolean,
	productTranslationService?: ProductTranslationService
) {
	if (variable === undefined) {
		return allowUndefinedCstValue || value !== undefined
			? new FieldTextCst(
				name,
				originalValue && productTranslationService
					? productTranslationService.translate(originalValue) : originalValue,
				value && productTranslationService ? productTranslationService.translate(value) : value
			)
			: undefined;
	}
	if (variable.free) {
		return new FieldTextVar(
			name,
			variable.path,
			originalValue && productTranslationService
				? productTranslationService.translate(originalValue)
				: originalValue,
			value && productTranslationService ? productTranslationService.translate(value) : value,
			variable.maxLength
		);
	}
	if (variableValues === undefined) {
		throw new Error(`set of available values for variable ${ name } not found`);
	}
	return new FieldTextSelection(
		name,
		variable.path,
		originalValue && productTranslationService ? removeTranslationCode(originalValue) : value,
		value && productTranslationService ? removeTranslationCode(value) : value,
		[
			{ name: 'product.most.used.values', values: variableValues.orderBookValues },
			{ name: 'product.other.values', values: variableValues.otherValues }
		]
	);
}

function createDefaultAtomicEntry(
	variable: TemplateVariable
) {
	if (variable.type === 'string') {
		return '';
	}
	if (variable.type === 'integer' || variable.type === 'number') {
		return 0;
	}
	throw new Error(`expected an atomic type, but found ${ variable.type } ${ variable.path }`);
}

function buildFieldSimpleObject(
	name: string,
	value: FieldValueObject,
	allowUndefinedCstValue: boolean,
	filterProperty?: (characteristicName: string) => boolean,
	productTranslationService?: ProductTranslationService
) {
	const fields: Field<FieldValue>[] = [];
	for (const fieldName of Object.getOwnPropertyNames(value)) {
		if (filterProperty && !filterProperty(fieldName)) {
			continue;
		}
		const fieldValue = value[fieldName];
		if (typeof fieldValue === 'number') {
			const fieldNumber = buildFieldNumber(
				fieldName,
				fieldValue,
				fieldValue,
				undefined,
				allowUndefinedCstValue
			);
			if (fieldNumber !== undefined) {
				fields.push(fieldNumber);
			}
			continue;
		}
		if (typeof fieldValue === 'string') {
			const fieldText = buildFieldText(
				fieldName,
				fieldValue,
				fieldValue,
				undefined,
				undefined,
				allowUndefinedCstValue,
				productTranslationService
			);
			if (fieldText !== undefined) {
				fields.push(fieldText);
			}
			continue;
		}
		throw new Error(
			`field value type for property of simple object not supported; expected number | string, but found ${
				JSON.stringify(fieldValue)
			}`
		);
	}
	return new FieldObjectCst(name, fields);
}

function buildFieldObject(
	soldTo: string | undefined,
	shipTo: string | undefined,
	endUser: string | undefined,
	name: string,
	originalProduct: CharacteristicSet | undefined,
	product: CharacteristicSet,
	variables: Map<string, TemplateVariable>,
	variableValues: Map<string, TemplateVariableValues>,
	allowUndefinedCstValue: boolean,
	filterProperty?: (characteristicName: string) => boolean,
	productTranslationService?: ProductTranslationService
) {
	const fields: Field<FieldValue>[] = [];
	const characteristics = new Set([...Array.from(variables.keys()), ...Object.getOwnPropertyNames(product)]);

	for (const chararacteristicName of characteristics) {
		if (filterProperty && !filterProperty(chararacteristicName)) {
			continue;
		}

		const characteristicValue = product[chararacteristicName];
		const variable = <TemplateVariableArray | undefined> variables.get(chararacteristicName);
		const type = characteristicValue !== undefined
			? typeof characteristicValue === 'object' && Array.isArray(characteristicValue)
				? 'array' : typeof characteristicValue : variable?.type;

		if (type === 'number' || type === 'integer') {
			const fieldNumber = buildFieldNumber(
				chararacteristicName,
				originalProduct === undefined
					? undefined
					: <number | undefined> originalProduct[chararacteristicName],
				typeof characteristicValue === 'number' ? characteristicValue : undefined,
				<TemplateVariableNumber | undefined> variables.get(chararacteristicName),
				allowUndefinedCstValue
			);
			if (fieldNumber !== undefined) {
				fields.push(fieldNumber);
			}
			continue;
		}
		if (type === 'string') {
			let variableValue: string | undefined;
			let originalVariableValue: string | CharacteristicSet | undefined;
			switch (chararacteristicName) {
			case FN_SOLD_TO_PARTY:
				variableValue = soldTo;
				originalVariableValue = soldTo;
				break;
			case FN_END_USER:
				variableValue = endUser;
				originalVariableValue = endUser;
				break;
			case FN_SHIP_TO_PARTY:
				variableValue = shipTo;
				originalVariableValue = shipTo;
				break;
			default:
				// eslint-disable-next-line @typescript-eslint/no-base-to-string
				variableValue = characteristicValue?.toString();
				originalVariableValue = originalProduct === undefined ? undefined
					: <string | undefined>originalProduct[chararacteristicName];
			}
			const fieldText = buildFieldText(
				chararacteristicName,
				originalProduct === undefined
					? undefined : variableValue,
				typeof characteristicValue === 'string' ? originalVariableValue : undefined,
					<TemplateVariableString | undefined>variables.get(chararacteristicName),
					variableValues.get(chararacteristicName),
					allowUndefinedCstValue,
					productTranslationService
			);
			if (fieldText !== undefined) {
				fields.push(fieldText);
			}
			continue;
		}
		if (type === 'array') {
			const orignalValue = originalProduct === undefined
				? undefined
				: <CharacteristicValueSimpleObject[] | undefined> originalProduct[chararacteristicName];

			const fieldList = variable === undefined
				? new FieldListCst(
					chararacteristicName,
					characteristicValue && Array.isArray(characteristicValue)
						? characteristicValue.map((entryValue: CharacteristicSet, i: number) => buildFieldObject(
							soldTo,
							shipTo,
							endUser,
							'entry',
							orignalValue === undefined || i >= orignalValue.length ? undefined : orignalValue[i],
							entryValue,
							new Map<string, TemplateVariable>(),
							variableValues,
							true,
							filterProperty,
							productTranslationService
						)) : [],
					entryValue => buildFieldSimpleObject(
						'entry',
						entryValue,
						allowUndefinedCstValue,
						filterProperty,
						productTranslationService
					)
				)
				: new FieldListVar(
					chararacteristicName,
					variable.path,
					characteristicValue && Array.isArray(characteristicValue)
						? characteristicValue.map((entryValue: CharacteristicSet, i: number) => buildFieldObject(
							soldTo,
							shipTo,
							endUser,
							'entry',
							orignalValue === undefined || i >= orignalValue.length ? undefined : orignalValue[i],
							entryValue,
							dynObjectToMap(variable.properties),
							variableValues,
							true,
							filterProperty,
							productTranslationService
						)) : [],
					() => {
						const defaultEntry: CharacteristicValueSimpleObject = {};
						for (const variableName of Object.getOwnPropertyNames(variable.properties)) {
							defaultEntry[variableName] = createDefaultAtomicEntry(variable.properties[variableName]);
						}
						return buildFieldObject(
							soldTo,
							shipTo,
							endUser,
							'entry',
							defaultEntry,
							defaultEntry,
							dynObjectToMap(variable.properties),
							variableValues,
							true,
							filterProperty,
							productTranslationService
						);
					},
					variable.maxItems
				);
			fields.push(fieldList);
			continue;
		}
		throw new Error(
			`characteristic value type not supported; expected number | string | SimpleObject[], but found ${
				JSON.stringify(characteristicValue)
			}`
		);
	}
	return new FieldObjectCst(name, fields);
}

export function buildProductCardFields(
	product: CharacteristicSet,
	soldTo: string,
	shipTo: string,
	endUser: string,
	filterProperty?: (characteristicName: string) => boolean
) {
	return buildFieldObject(
		soldTo,
		shipTo,
		endUser,
		'product',
		product,
		product,
		new Map<string, TemplateVariable>(),
		new Map<string, TemplateVariableValues>(),
		false,
		filterProperty
	);
}

export function buildEditionProductCardFields(
	soldTo: string | undefined,
	shipTo: string | undefined,
	endUser: string | undefined,
	originalProduct: CharacteristicSet,
	product: CharacteristicSet,
	variables: Map<string, TemplateVariable>,
	variableValues: Map<string, TemplateVariableValues>,
	filterProperty: (characteristicName: string) => boolean,
	productTranslationService?: ProductTranslationService
) {
	return buildFieldObject(
		soldTo,
		shipTo,
		endUser,
		'product',
		originalProduct,
		product,
		variables,
		variableValues,
		false,
		filterProperty,
		productTranslationService
	);
}
